import { ScreenHeader } from '@/components/layout/screen-header';
import { Text } from '@/components/ui/text';
import { FacetChipGrid } from '@/features/taste-profile/components/facet-chip-grid';
import { useTasteProfile } from '@/features/taste-profile/hooks/use-taste-profile';
import { cuisineListQueryOptions } from '@/api/queries/cuisine';
import { allergenListQueryOptions } from '@/api/queries/allergen';
import { dietaryProfileListQueryOptions } from '@/api/queries/dietary-profile';
import type { CuisineValence, TasteRef } from '@/types/taste-profile';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, TextInput, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const LOAD_ERROR = 'We could not load your taste profile. Please try again.';

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <View className="mb-2 mt-6">
      <Text className="text-base font-semibold text-foreground">{title}</Text>
      {subtitle ? <Text className="text-sm text-muted-foreground mt-0.5">{subtitle}</Text> : null}
    </View>
  );
}

/**
 * Standalone editor for the user's Taste Profile (cuisines favorite/avoid,
 * allergens, dietary profiles). Live optimistic edits via useTasteProfile;
 * no Save button. Cuisines cycle on tap: favorite -> avoid -> cleared.
 */
export function TasteProfileScreen() {
  const {
    data,
    isLoading,
    isError,
    toggleCuisine,
    removeCuisine,
    toggleAllergen,
    toggleDietary,
  } = useTasteProfile();
  const [cuisineQuery, setCuisineQuery] = useState('');

  const cuisines = useQuery(cuisineListQueryOptions({ query: cuisineQuery.trim() || undefined }));
  const allergens = useQuery(allergenListQueryOptions());
  const dietary = useQuery(dietaryProfileListQueryOptions());

  const cuisineState = (id: string): 'none' | CuisineValence =>
    data.cuisines.find((c) => c.id === id)?.valence ?? 'none';

  const onCuisinePress = (cuisine: TasteRef) => {
    const state = cuisineState(cuisine.id);
    if (state === 'none') toggleCuisine(cuisine, 'favorite');
    else if (state === 'favorite') toggleCuisine(cuisine, 'avoid');
    else removeCuisine(cuisine.id);
  };

  const allergenSelected = (a: TasteRef) => data.allergens.some((x) => x.id === a.id);
  const dietarySelected = (d: TasteRef) => data.dietaryProfiles.some((x) => x.id === d.id);

  return (
    <SafeAreaView className="flex-1" edges={['top']}>
      <ScreenHeader title="Taste Profile" showBackButton />
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="px-4 pb-8"
      >
        <Text className="text-base mt-2 text-muted-foreground">
          Tell us what you love. Your taste profile personalizes search and recommendations across
          Flavrly.
        </Text>

        {isLoading && (
          <View className="py-8 items-center" accessibilityLiveRegion="polite">
            <ActivityIndicator />
            <Text className="text-base text-muted-foreground mt-3 text-center">
              Loading your taste profile...
            </Text>
          </View>
        )}

        {isError && !isLoading && (
          <Text
            className="text-base text-destructive text-center py-8"
            accessibilityLiveRegion="polite"
          >
            {LOAD_ERROR}
          </Text>
        )}

        {!isLoading && !isError && (
          <View>
            <SectionHeading
              title="Cuisines"
              subtitle="Tap once to favorite, again to avoid, again to clear."
            />
            <TextInput
              testID="cuisine-search"
              value={cuisineQuery}
              onChangeText={setCuisineQuery}
              placeholder="Search cuisines"
              className="rounded-lg border border-border px-3 py-2 mb-3 text-foreground"
              accessibilityLabel="Search cuisines"
            />
            <View className="flex-row flex-wrap gap-2">
              {(cuisines.data?.items ?? []).map((cuisine) => {
                const state = cuisineState(cuisine.id);
                const selected = state !== 'none';
                const tone =
                  state === 'favorite'
                    ? 'border-primary bg-primary/10'
                    : state === 'avoid'
                      ? 'border-destructive bg-destructive/10'
                      : 'border-border';
                const textTone =
                  state === 'favorite'
                    ? 'text-primary font-semibold'
                    : state === 'avoid'
                      ? 'text-destructive font-semibold'
                      : 'text-foreground';
                return (
                  <Pressable
                    key={cuisine.id}
                    testID={`cuisine-chip-${cuisine.slug}`}
                    onPress={() =>
                      onCuisinePress({ id: cuisine.id, slug: cuisine.slug, name: cuisine.name })
                    }
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    accessibilityValue={{ text: state }}
                    hitSlop={8}
                    className={`rounded-full border px-3 py-1.5 ${tone}`}
                  >
                    <Text className={`text-sm ${textTone}`}>{cuisine.name}</Text>
                  </Pressable>
                );
              })}
            </View>

            <SectionHeading title="Allergies" subtitle="Foods we will keep away from you." />
            <FacetChipGrid
              options={(allergens.data?.items ?? []).map((a) => ({ id: a.id, slug: a.slug, name: a.name }))}
              isSelected={allergenSelected}
              onToggle={toggleAllergen}
              testIdPrefix="allergen-chip"
            />

            <SectionHeading title="How you eat" subtitle="Dietary patterns you follow." />
            <FacetChipGrid
              options={(dietary.data?.items ?? []).map((d) => ({ id: d.id, slug: d.slug, name: d.name }))}
              isSelected={dietarySelected}
              onToggle={toggleDietary}
              testIdPrefix="dietary-chip"
            />
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
